'use client';

import { createContext, useContext, useState } from 'react';

import { ApiSchemas, useApiV1Ctx } from '@/components/providers/v1-api';
import { Skeleton } from '@/components/ui/skeleton';

type ServicePlanTemplate = ApiSchemas['ServicePlanTemplateRead'];
type ServicePlanTemplateTabs = 'details' | 'service-types';

interface Context {
  servicePlanTemplate: ServicePlanTemplate;
  tab: ServicePlanTemplateTabs;
  setTab: (tab: ServicePlanTemplateTabs) => void;
  refetch: () => void;
}

const ServicePlanTemplateContext = createContext<Context | null>(null);

export function useServicePlanTemplateCtx() {
  const ctx = useContext(ServicePlanTemplateContext);
  if (!ctx) {
    throw new Error('useServicePlanTemplateCtx must be used within a ServicePlanTemplateProvider');
  }
  return ctx;
}

export function ServicePlanTemplateProvider({ id, children }: { id: string; children: React.ReactNode }) {
  const { api } = useApiV1Ctx();
  const [tab, setTab] = useState<ServicePlanTemplateTabs>('details');
  const { data, refetch, isPending } = api.servicePlanTemplates.getServicePlanTemplatesTemplateIdGet.useQuery({
    path: { template_id: id },
  });

  if (isPending) {
    return <Skeleton className='h-10 w-full' />;
  }

  if (!data) {
    return <div>Service plan template not found</div>;
  }

  return (
    <ServicePlanTemplateContext.Provider value={{ servicePlanTemplate: data, tab, setTab, refetch }}>
      {children}
    </ServicePlanTemplateContext.Provider>
  );
}
